import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { usePlanStatus } from './usePlanStatus'
import { useGroceryList } from '@/features/grocery-list/useGroceryList'
import { useGenerateGroceryList } from '@/features/grocery-list/useGenerateGroceryList'
import { useMealPlan } from '@/features/meal-plan/useMealPlan'
import { useUserRole } from '@/shared/hooks/useUserRole'
import { useWakeLock } from '@/features/grocery-list/useWakeLock'
import { useGroceryListRealtime } from '@/features/grocery-list/useGroceryListRealtime'
import { useOfflineSync } from '@/features/grocery-list/useOfflineSync'
import { useTickGroceryItem } from '@/features/grocery-list/useTickGroceryItem'
import { CategorySection } from '@/features/grocery-list/CategorySection'
import { GroceryListSyncBanner } from '@/features/grocery-list/GroceryListSyncBanner'
import { getMondayOfWeek } from '@/features/meal-plan/utils'
import { PlanningNudge } from './PlanningNudge'
import { WeekSummary } from './WeekSummary'
import { GroceryProgress } from './GroceryProgress'

// Local type for grocery items — avoids cross-feature type import
interface GroceryItemLike {
  id: string
  ingredientId: string | null
  ingredientName: string
  quantity: number
  canonicalUnit: string
  aisleCategory: string
  ticked: boolean
}

const AISLE_ORDER = [
  'Produce',
  'Meat & Fish',
  'Dairy',
  'Bakery',
  'Tinned & Dry Goods',
  'Household',
] as const

function HomeScreenSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4" role="status">
      <div className="h-8 bg-gray-100 rounded animate-pulse" />
      <div className="h-24 bg-gray-100 rounded animate-pulse" />
      <div className="h-10 bg-gray-100 rounded animate-pulse" />
    </div>
  )
}

function EmbeddedGroceryList({ showProgress }: { showProgress: boolean }) {
  const { t } = useTranslation('grocery-list')
  const { data } = useGroceryList()

  useWakeLock()
  useGroceryListRealtime()
  useOfflineSync()
  const { handleTick } = useTickGroceryItem()

  const items = (data?.items ?? []) as GroceryItemLike[]
  const total = items.length
  const ticked = items.filter(i => i.ticked).length

  const grouped = useMemo(() => {
    const map = new Map<string, GroceryItemLike[]>()
    for (const item of items) {
      const bucket = map.get(item.aisleCategory) ?? []
      bucket.push(item)
      map.set(item.aisleCategory, bucket)
    }
    return map
  }, [items])

  return (
    <div className="flex flex-col h-full">
      <GroceryListSyncBanner />
      {showProgress && <GroceryProgress total={total} ticked={ticked} />}
      <div
        aria-label={t('listAriaLabel')}
        className="overflow-y-auto flex-1"
      >
        {AISLE_ORDER
          .filter(aisle => (grouped.get(aisle)?.length ?? 0) > 0)
          .map(aisle => (
            <CategorySection
              key={aisle}
              category={t(`aisleCategory.${aisle}`)}
              items={grouped.get(aisle)!}
              onTick={handleTick}
            />
          ))
        }
        {[...grouped.keys()]
          .filter(key => !(AISLE_ORDER as readonly string[]).includes(key))
          .map(key => (
            <CategorySection
              key={key}
              category={key}
              items={grouped.get(key)!}
              onTick={handleTick}
            />
          ))
        }
      </div>
    </div>
  )
}

function WeekSummaryContainer() {
  const weekStartDate = useMemo(() => getMondayOfWeek(), [])
  const { data: plan } = useMealPlan(weekStartDate)
  const { mutate: generate, isPending } = useGenerateGroceryList()
  const role = useUserRole()
  const isPlanner = role === 'Planner'

  if (!plan) return null

  return (
    <WeekSummary
      plan={plan}
      onGenerate={() => generate()}
      isGenerating={isPending}
      isPlanner={isPlanner}
    />
  )
}

export function ContextualHomeHero() {
  const { status, isLoading } = usePlanStatus()

  if (isLoading) return <HomeScreenSkeleton />

  switch (status) {
    case 'empty':
      return <PlanningNudge />
    case 'partial':
      return <WeekSummaryContainer />
    case 'generated':
      return <EmbeddedGroceryList showProgress={false} />
    case 'in-progress':
      return <EmbeddedGroceryList showProgress={true} />
  }
}
